import { useState, useEffect } from 'react';
import { useRealtimeStore } from '../store/realtimeStore';
import { formatDistanceToNow } from 'date-fns';
import {
  BellIcon,
  XMarkIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import { BellAlertIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';

export interface Notification {
  id: string;
  type: 'USER_MESSAGE' | 'ALERT' | 'SOS' | 'SYSTEM' | 'TRACKING_ALERT';
  title: string;
  message: string;
  senderName?: string;
  senderEmail?: string;
  timestamp: Date;
  read: boolean;
}

export function NotificationPanel() {
  const [isOpen, setIsOpen] = useState(false);
  const { notifications, markNotificationRead, clearNotifications } = useRealtimeStore();
  
  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="relative z-[9999]">
      {/* Notification Bell */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2.5 rounded-lg hover:bg-gray-700 transition-colors touch-manipulation"
        aria-label="Notifications"
      >
        {unreadCount > 0 ? (
          <BellAlertIcon className="w-6 h-6 text-yellow-400 animate-pulse" />
        ) : (
          <BellIcon className="w-6 h-6 text-gray-400" />
        )}
        
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0" 
            style={{ zIndex: 99998 }}
            onClick={() => setIsOpen(false)}
          />
          
          {/* Panel */}
          <div
            className="fixed left-2 right-2 top-16 sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-96 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl overflow-hidden"
            style={{ zIndex: 99999 }}
          >
            {/* Header */}
            <div className="px-4 py-3 border-b border-gray-700 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <EnvelopeIcon className="w-5 h-5 text-blue-400" />
                <h3 className="font-semibold text-white">Notifications</h3>
                {unreadCount > 0 && (
                  <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full">
                    {unreadCount} new
                  </span>
                )}
              </div>
              
              {notifications.length > 0 && (
                <button
                  onClick={() => clearNotifications()}
                  className="text-xs text-gray-400 hover:text-white transition-colors"
                >
                  Clear all
                </button>
              )}
            </div>

            {/* Notifications List */}
            <div className="max-h-[min(400px,65dvh)] overflow-y-auto overscroll-contain">
              {notifications.length === 0 ? (
                <div className="py-12 text-center">
                  <BellIcon className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-500">No notifications yet</p>
                  <p className="text-gray-600 text-sm mt-1">
                    Messages from the field will appear here
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-gray-800">
                  {notifications.map((notification) => (
                    <NotificationItem
                      key={notification.id}
                      notification={notification}
                      onMarkRead={() => markNotificationRead(notification.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function NotificationItem({ 
  notification, 
  onMarkRead 
}: { 
  notification: Notification;
  onMarkRead: () => void;
}) {
  const typeColors = {
    USER_MESSAGE: 'bg-blue-500',
    PC_MESSAGE: 'bg-blue-500',
    ALERT: 'bg-yellow-500',
    SOS: 'bg-red-500',
    PC_SOS: 'bg-red-500',
    SYSTEM: 'bg-gray-500',
    TRACKING_ALERT: 'bg-green-500',
  };

  const typeIcons = {
    USER_MESSAGE: '💬',
    PC_MESSAGE: '💬',
    ALERT: '⚠️',
    SOS: '🚨',
    PC_SOS: '🚨',
    SYSTEM: '⚙️',
    TRACKING_ALERT: '📍',
  };

  return (
    <div
      className={clsx(
        'px-4 py-3 hover:bg-gray-800/50 transition-colors cursor-pointer',
        !notification.read && 'bg-blue-500/5'
      )}
      onClick={onMarkRead}
    >
      <div className="flex gap-3">
        {/* Type indicator */}
        <div className={clsx(
          'w-10 h-10 rounded-full flex items-center justify-center text-lg flex-shrink-0',
          typeColors[notification.type] + '/20'
        )}>
          {typeIcons[notification.type]}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className={clsx(
                'font-medium text-sm',
                notification.read ? 'text-gray-300' : 'text-white'
              )}>
                {notification.title}
              </p>
              {notification.senderName && (
                <p className="text-xs text-gray-500">
                  From: {notification.senderName}
                </p>
              )}
            </div>
            
            {!notification.read && (
              <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1.5" />
            )}
          </div>
          
          <p className="text-sm text-gray-400 mt-1 line-clamp-2">
            {notification.message}
          </p>
          
          <p className="text-xs text-gray-500 mt-2">
            {formatDistanceToNow(new Date(notification.timestamp), { addSuffix: true })}
          </p>
        </div>
      </div>
    </div>
  );
}

// Toast notification that appears when new message arrives
export function NotificationToast({ 
  notification, 
  onClose 
}: { 
  notification: Notification;
  onClose: () => void;
}) {
  useEffect(() => {
    const timer = setTimeout(onClose, 8000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="fixed top-4 left-4 right-4 sm:left-auto sm:right-4 z-[9999] animate-slide-in">
      <div className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl p-4 sm:max-w-sm">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center text-xl">
            💬
          </div>
          
          <div className="flex-1">
            <div className="flex items-start justify-between">
              <div>
                <p className="font-semibold text-white">{notification.title}</p>
                {notification.senderName && (
                  <p className="text-xs text-blue-400">From: {notification.senderName}</p>
                )}
              </div>
              <button
                onClick={onClose}
                className="p-1 hover:bg-gray-700 rounded transition-colors"
              >
                <XMarkIcon className="w-4 h-4 text-gray-400" />
              </button>
            </div>
            
            <p className="text-sm text-gray-300 mt-2 line-clamp-3">
              {notification.message}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

